/*
  CUSTOM 2026-08-07 — Unterhalb von 1300px alle Nicht-Farb-Optionen als Auswahlliste.
  CUSTOM 2026-08-14 — Auswahlliste liegt jetzt im Shadow DOM. Begruendung unten.

  Die Grenze steht doppelt: hier und als Medienabfrage in snippets/css-variables.liquid
  (Regeln .custom-variant-dropdown / .custom-variant-dropdown-aktiv). Medienabfragen koennen
  keine CSS-Variablen auswerten, deshalb laesst sich das nicht an einer Stelle buendeln —
  wird die Grenze geaendert, muss sie an BEIDEN Stellen geaendert werden.

  Warum nicht in Liquid: Ob Knopfreihe oder Auswahlliste, entscheidet
  snippets/product-variant-picker-swatch.liquid beim Rendern auf dem Server. Die Fensterbreite
  ist dort nicht bekannt — dieselbe Seite wird an Handy und Rechner ausgeliefert.

  ------------------------------------------------------------------------------------------
  WARUM DAS AUSWAHLFELD IM SHADOW DOM LIEGEN MUSS

  Fehlermeldung Paul Doering (04.08.2026): Auf dem Handy springt die Tiefe auf 20 cm zurueck
  und die anderen Werte lassen sich nicht waehlen; am Rechner geht alles.

  Die erste Fassung hat das <select> normal in den Fieldset gehaengt. assets/theme.js (Klasse
  VariantPicker) durchsucht den Bereich aber mit drei Abfragen, die genau darauf ansprechen:

    1. syncCurrentOptions    → querySelectorAll('select, fieldset')
       Unser <select> zaehlte als zusaetzliche Optionsgruppe. Die Liste der aktuellen Auswahl
       bekam einen Eintrag zu viel, und findClosestAvailableVariant vergleicht anschliessend
       Position fuer Position gegen option1/option2/option3 — alles verschoben.

    2. updateAvailableOptions → querySelectorAll('fieldset, .variant-wrapper')
       Die Huelle trug die Klasse `variant-wrapper`. Aus drei Gruppen wurden sechs; der Index i
       steuert dort, welche Option geprueft wird, und setInputStates setzt am Ende
       `input.checked = true`. Das Theme hakte selbst den falschen Wert an — der Sprung auf 20.

    3. setInputStates        → group.querySelectorAll('input[type="radio"], option')
       Unsere <option> lagen im Fieldset und wurden mitgefasst. Das Theme setzte darauf das
       Attribut `selected`; gelesen wird die Auswahl ueber
       `select option[selected], fieldset input:checked`. Jede Option kam doppelt an.

  Am Rechner blieb alles heil, weil das <select> nur auf schmalen Displays gebaut wurde.

  querySelectorAll durchsucht KEINE Shadow Roots. Liegt das Auswahlfeld darin, existiert es
  fuer alle drei Abfragen nicht — das Theme sieht exakt die Seite, die es ohne dieses Skript
  auch saehe. Bedient wird weiterhin der Radio des Themes.

  Zusaetzlich traegt die Huelle NICHT mehr die Klasse `variant-wrapper` (Punkt 2), und im
  Licht-DOM steht kein einziges <option> (Punkt 3).

  ------------------------------------------------------------------------------------------
  WEITERE REGELN, die hier absichtlich so stehen:

  - Nichts wird zwischengespeichert. Nach einem Variantenwechsel tauscht die Section Rendering
    API Teile des Bereichs aus; gemerkte Element-Listen zeigen danach ins Leere.
  - Werte werden NIE gesperrt. Das Theme markiert nicht lieferbare Werte nur mit der Klasse
    `disabled` (snippets/product-variant-picker-swatch.liquid, Zeile 174) — anklickbar bleiben
    sie. Ein echtes disabled am <option> machte sie unwaehlbar; am Rechner ging es weiter.
  - Farboptionen bleiben Farbfelder: in einer Textliste ginge die Farbinformation verloren.

  | Dependency: snippets/product-variant-picker-swatch.liquid (fieldset[data-option-slug]),
  |             snippets/css-variables.liquid (.custom-variant-dropdown)
*/
(() => {
  if (window.__customVariantDropdown) return;
  window.__customVariantDropdown = true;

  /* 1299px statt 1300px: Medienabfragen sind einschliesslich, sonst waeren 1300px selbst
     noch "schmal". Ab 1300px greift wieder die serverseitige Regel (Auswahlliste erst ab mehr
     als vier Werten, siehe snippets/product-variant-picker-swatch.liquid). */
  const GRENZE = '(max-width: 1299px)';

  /* Eigene Gestaltung, weil die Theme-Klassen im Shadow DOM nicht greifen. CSS-Variablen
     werden dagegen vererbt — deshalb sehen Rahmen, Rundung und Schrift aus wie bei den
     uebrigen Feldern des Themes. */
  const STIL = `
    :host { display: block; }
    select {
      font: inherit;
      color: rgb(var(--color-foreground));
      width: 100%;
      min-height: var(--input-height, 3rem);
      padding: var(--sp-2d5, 0.625rem) var(--sp-4, 1rem);
      background-color: rgb(var(--color-background));
      border: var(--inputs-border-width, 1px) solid rgb(var(--color-foreground) / 0.25);
      border-radius: var(--inputs-radius, 4px);
    }
    select:focus-visible { outline: 2px solid rgb(var(--color-foreground) / 0.5); outline-offset: 2px; }
  `;

  const istFarbe = (feld) => {
    /* Farboptionen rendern <label class="color-swatch"> mit der Farbe als CSS-Variable
       (--swatch-background). Textoptionen wie "Tiefe" nutzen dieselbe <ul class="swatches">,
       aber gewoehnliche Textlabels — deshalb reicht die Klasse `swatches` als Merkmal nicht. */
    return Boolean(feld.querySelector('.color-swatch, [style*="--swatch-background"]'));
  };

  const beschriftung = (eingabe) => {
    const label = eingabe.closest('li, div')?.querySelector('label');
    const text = (label?.textContent || eingabe.value || '').replace(/\s+/g, ' ').trim();
    return text || eingabe.value;
  };

  const radios = (feld) => [...feld.querySelectorAll('input[type="radio"]')];

  /* Eintraege und ausgewaehlten Wert an den aktuellen Stand der Radios angleichen. Wird nach
     jedem Variantenwechsel aufgerufen; erzeugt nur dann neue Eintraege, wenn sich die Werte
     tatsaechlich geaendert haben. */
  const angleichen = (liste, feld) => {
    const eingaben = radios(feld);
    const soll = eingaben.map((eingabe) => eingabe.value).join(' ');

    if (liste.dataset.werte !== soll) {
      liste.dataset.werte = soll;
      liste.replaceChildren();
      eingaben.forEach((eingabe) => {
        const eintrag = document.createElement('option');
        eintrag.value = eingabe.value;
        eintrag.textContent = beschriftung(eingabe);
        liste.append(eintrag);
      });
    }

    const gewaehlt = eingaben.find((eingabe) => eingabe.checked);
    if (gewaehlt && liste.value !== gewaehlt.value) liste.value = gewaehlt.value;
  };

  const aufbauen = (feld) => {
    if (istFarbe(feld)) return;
    if (radios(feld).length < 2) return;

    let huelle = feld.querySelector(':scope > .custom-variant-dropdown');

    if (!huelle) {
      huelle = document.createElement('div');
      /* Bewusst NICHT `variant-wrapper` — diese Klasse sucht updateAvailableOptions ab. */
      huelle.className = 'custom-variant-dropdown';

      const wurzel = huelle.attachShadow({ mode: 'open' });
      const stil = document.createElement('style');
      stil.textContent = STIL;

      const liste = document.createElement('select');
      liste.setAttribute(
        'aria-label',
        feld.querySelector('legend, .form__label')?.textContent?.trim() || 'Auswahl'
      );

      /* Das change-Ereignis eines <select> ist nicht `composed` und verlaesst den Shadow Root
         nicht — der VariantPicker bekommt es also gar nicht erst zu sehen. Ausgeloest wird der
         Wechsel ausschliesslich ueber den Radio des Themes, wie beim Klick am Rechner.
         Die Radios werden hier frisch abgefragt: Die beim Aufbau bekannten Elemente haengen
         nach einem Variantenwechsel womoeglich nicht mehr in der Seite. */
      liste.addEventListener('change', () => {
        const treffer = radios(feld).find((eingabe) => eingabe.value === liste.value);
        if (treffer && !treffer.checked) treffer.click();
      });

      wurzel.append(stil, liste);
      feld.append(huelle);
    }

    angleichen(huelle.shadowRoot.querySelector('select'), feld);
  };

  const anwenden = () => {
    const schmal = window.matchMedia(GRENZE).matches;
    document.querySelectorAll('variant-picker fieldset[data-option-slug]').forEach((feld) => {
      if (schmal) aufbauen(feld);
      const huelle = feld.querySelector(':scope > .custom-variant-dropdown');
      feld.classList.toggle('custom-variant-dropdown-aktiv', schmal && Boolean(huelle));
    });
  };

  /* Das Theme hakt beim Variantenwechsel selbst Radios an — die Auswahlliste zieht nach. */
  document.addEventListener('change', (event) => {
    const ziel = event.target instanceof Element ? event.target : null;
    const feld = ziel?.closest('variant-picker fieldset[data-option-slug]');
    const liste = feld?.querySelector(':scope > .custom-variant-dropdown')?.shadowRoot?.querySelector('select');
    if (liste) angleichen(liste, feld);
  });

  const start = () => {
    anwenden();
    window.matchMedia(GRENZE).addEventListener('change', anwenden);

    /* Nach Varianten- und Mengenwechsel tauscht die Section Rendering API den Bereich aus.
       Der Beobachter wird waehrend der eigenen Aenderungen abgeschaltet, sonst ruft er sich
       selbst wieder auf. */
    if ('MutationObserver' in window) {
      const beobachter = new MutationObserver(() => {
        beobachter.disconnect();
        anwenden();
        beobachter.observe(document.documentElement, { childList: true, subtree: true });
      });
      beobachter.observe(document.documentElement, { childList: true, subtree: true });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
